import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject, switchMap } from 'rxjs';
import { environment } from 'environments/environment';
import { IFiltersForm } from 'app/models/filter-form.models';
import {
  IAreaFilterValues,
  IAutocompleteOption,
  IFilters,
  IOfficerNamesRequest,
  IPersonnelOfficer,
  IVehicleFilterValues,
} from 'app/models/filter.models';
import { OfficerNamesResponse } from 'app/models/personnel.models';
import { AuthService } from './auth.service';
import { StorageKeys } from 'app/constants/common.constants';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private internalAppliedFilters!: IFilters;

  readonly applyFiltersChange: Subject<IFiltersForm> = new Subject();

  get appliedFilters(): IFilters {
    return this.internalAppliedFilters;
  }

  // we shouldn't send department id from personnel filter to backend
  get filters(): IFilters {
    const personnelFilter = {
      officers: this.internalAppliedFilters.personnelFilter?.officers,
      units: this.internalAppliedFilters.personnelFilter?.units,
    };

    return {
      ...this.internalAppliedFilters,
      personnelFilter,
    };
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {
    const filters = sessionStorage.getItem(StorageKeys.FILTERS);
    this.internalAppliedFilters = filters ? JSON.parse(filters) : {};

    this.applyFiltersChange.subscribe((values: IFilters): void => {
      this.internalAppliedFilters = values;
      sessionStorage.setItem(StorageKeys.FILTERS, JSON.stringify(this.internalAppliedFilters));
    });
  }

  applyAllFilters(values: IFilters): void {
    this.applyFiltersChange.next(values);
  }

  applyFilters(values: IFiltersForm) {
    this.applyFiltersChange.next(this.mapFilters(values));
  }

  getAreas(id?: string): Observable<IAreaFilterValues> {
    const url = `${environment.apiUrl}/api/analytics/filters/location`;
    let params = new HttpParams();

    if (!this.authService.isAdmin) {
      params = params.set('departmentId', this.authService.getDepartmentId());
    } else if (id) {
      params = params.set('departmentId', id);
    }

    return this.http.get<IAreaFilterValues>(url, { params }).pipe(
      catchError(() => {
        return of();
      }),
    );
  }

  getVehicles(): Observable<IVehicleFilterValues> {
    return this.http.get<IVehicleFilterValues>(`${environment.apiUrl}/api/analytics/filters/vehicle`).pipe(
      catchError(() => {
        return of();
      }),
    );
  }

  getViolationDescriptions() {
    return this.http.get<string[]>(`${environment.apiUrl}/api/analytics/violations/police-codes`).pipe(
      catchError(() => {
        return of();
      }),
    );
  }

  getOffenceDescriptions() {
    return this.http.get<string[]>(`${environment.apiUrl}/api/analytics/violations/names`).pipe(
      catchError(() => {
        return of();
      }),
    );
  }

  getStopDetails() {
    return this.http
      .get<{ name: string; values: string[] }[]>(`${environment.apiUrl}/api/analytics/filters/stop-details`)
      .pipe(
        catchError(() => {
          return of();
        }),
      );
  }

  getPersonnel(isAdmin: boolean, departmentId?: string): Observable<IPersonnelOfficer[]> {
    let params = new HttpParams();
    if (isAdmin && departmentId) {
      params = params.append('departmentId', departmentId);
    }

    return this.http
      .get<OfficerNamesResponse>(
        isAdmin
          ? `${environment.leoApiUrl}/api/v1/officer/full-names`
          : `${environment.leoApiUrl}/api/v1/officer/current-department/full-names`,
        { params },
      )
      .pipe(
        map((officers) =>
          officers.sort((a, b) => {
            const nameA = a.officerName || '';
            const nameB = b.officerName || '';
            return nameA.localeCompare(nameB);
          }),
        ),
        catchError(() => {
          return of([]);
        }),
      );
  }

  getOfficers(search = ''): Observable<IOfficerNamesRequest> {
    const params = new HttpParams().appendAll({
      page: 0,
      size: 10,
      sort: 'asc',
      search,
    });

    return this.http.get<IOfficerNamesRequest>(`${environment.apiUrl}/api/analytics/officers/names`, { params }).pipe(
      catchError(() => {
        return of();
      }),
    );
  }

  getDefaultValues(): Observable<{
    departmentId: string;
    city: string | undefined;
  }> {
    return this.getAreas().pipe(
      switchMap((filters) => {
        return this.authService.getDepartment().pipe(
          map(({ id }) => {
            const city = filters.cities.length === 1 ? filters.cities[0] : undefined;

            return { departmentId: id, city };
          }),
        );
      }),
    );
  }

  private mapFilters(filters: IFiltersForm): IFilters {
    const fromDate = filters.citationDateGroup?.fromDate
      ? moment(filters.citationDateGroup.fromDate).format('YYYY-MM-DD')
      : undefined;
    const toDate = filters.citationDateGroup?.fromDate
      ? moment(filters.citationDateGroup?.toDate).format('YYYY-MM-DD')
      : undefined;

    const citationTypes = Object.entries(filters.offenceDescGroup ?? {})
      .filter(([, value]) => value)
      .map(([key]) => key);

    const officer = filters?.personalGroup?.officer;
    const personalFilter = officer?.id
      ? {
          officerId: Number(officer.id),
          officerName: officer.value,
        }
      : {};

    const hoursFrom = filters.timeGroup?.hoursFrom;
    const minutesFrom = filters.timeGroup?.minutesFrom;

    const hoursTo = filters.timeGroup?.hoursTo;
    const minutesTo = filters.timeGroup?.minutesTo;

    let fromTime;
    if (hoursFrom && minutesFrom) {
      fromTime = `${hoursFrom}:${minutesFrom}`;
    }

    let toTime;
    if (hoursTo && minutesTo) {
      toTime = `${hoursTo}:${minutesTo}`;
    }

    const { releaseDateFrom: rawReleaseDateFrom, releaseDateTo: rawReleaseDateTo } = filters.vehiclesGroup || {};
    const releaseDateFrom = rawReleaseDateFrom ? moment(rawReleaseDateFrom).format('YYYY') : undefined;
    const releaseDateTo = rawReleaseDateTo ? moment(rawReleaseDateTo).format('YYYY') : undefined;

    let personnelFilter;

    if (this.authService.isOperationsRoles) {
      const officers = cleanEmptyArrays(
        filters.personnelGroup?.officers?.map(({ id, value }) => ({
          keycloakId: id,
          fullName: value,
        })),
      );

      personnelFilter = {
        departmentId: filters.personnelGroup?.departmentId,
        officers,
        units: cleanEmptyArrays(getValuesFromOptions(filters.personnelGroup?.units)),
      };
    }

    return {
      departmentId: filters.areaGroup?.departmentId,
      dateFilter: {
        fromDate,
        toDate,
        fromTime,
        toTime,
        daysOfWeek: cleanEmptyArrays(filters.dateTimeGroup?.daysOfWeek),
        months: cleanEmptyArrays(filters.dateTimeGroup?.months),
      },
      demographicFilter: {
        races: cleanEmptyArrays(filters.demographicsGroup?.races),
        gender: filters.demographicsGroup?.gender,
        ageGroup: filters.demographicsGroup?.ageGroup,
      },
      vehicleFilter: {
        releaseDateFrom,
        releaseDateTo,
        licensePlate: filters.vehiclesGroup?.licensePlate,
        state: filters.vehiclesGroup?.state,
        colors: cleanEmptyArrays(getValuesFromOptions(filters.vehiclesGroup?.colors)),
        makes: cleanEmptyArrays(getValuesFromOptions(filters.vehiclesGroup?.makes)),
        models: cleanEmptyArrays(getValuesFromOptions(filters.vehiclesGroup?.models)),
        types: cleanEmptyArrays(getValuesFromOptions(filters.vehiclesGroup?.types)),
      },
      locationFilter: {
        zipCodes: cleanEmptyArrays(getValuesFromOptions(filters.areaGroup?.zipCodes)),
        cities: cleanEmptyArrays(getValuesFromOptions(filters.areaGroup?.cities)),
      },
      stopDetailsFilter: {
        reasonsForSearch: cleanEmptyArrays(filters.stopDetailsGroup?.reasonForSearch),
        reasonsForStop: cleanEmptyArrays(filters.stopDetailsGroup?.reasonForStop),
        contrabandOrEvidence: cleanEmptyArrays(filters.stopDetailsGroup?.contrabandOrEvidence),
        resultsOfStop: cleanEmptyArrays(filters.stopDetailsGroup?.resultOfStop),
      },
      personnelFilter,
      personalFilter,
      violationFilter: {
        names: cleanEmptyArrays(citationTypes),
        policeCodes: cleanEmptyArrays(this.internalAppliedFilters?.violationFilter?.policeCodes),
      },
      citationFilter: this.internalAppliedFilters?.citationFilter,
      coordinatesFilter: this.internalAppliedFilters?.coordinatesFilter,
      polygonFilter: this.internalAppliedFilters?.polygonFilter,
    };
  }
}

function cleanEmptyArrays<T>(array: T[] | undefined) {
  if (array?.length === 0) return undefined;
  return array;
}

function getValuesFromOptions(options: IAutocompleteOption[] | undefined = []): string[] {
  return options
    ?.map(({ value }) => value)
    .filter((element) => element !== undefined && element !== '' && element !== null);
}
